import { Link } from "react-router-dom";
import type { Appointment } from "../../lib/api";
import { ServiceBadge, TrainerBadge, LocationIcon } from "./index";

interface AppointmentCardProps {
  appointment: Appointment;
  showTrainerBadge?: boolean;
  onClick?: () => void;
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function getTrainerInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.trim().split(" ").filter((p) => p.length > 0);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return (parts[0] ?? "").substring(0, 2).toUpperCase();
  const first = parts[0] ?? "";
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

function getLocationLabel(appointment: Appointment): string {
  switch (appointment.locationType) {
    case "client_home":
      return "Home Visit";
    case "facility":
      return appointment.savedLocationName || "Facility";
    case "saved_location":
      return appointment.savedLocationName || "Meeting Point";
    case "virtual":
      return "Virtual";
    default:
      return "TBD";
  }
}

export function AppointmentCard({
  appointment,
  showTrainerBadge = true,
  onClick,
}: AppointmentCardProps) {
  const startTime = formatTime(appointment.startTime);
  const endTime = formatTime(appointment.endTime);
  const borderColor = showTrainerBadge
    ? appointment.calendarColor || "#6366F1"
    : appointment.serviceColor || "#64748B";

  const hasAlert = false; // TODO: Add alert logic based on notes/behavioral data

  const clientName =
    appointment.clientFirstName && appointment.clientLastName
      ? `${appointment.clientFirstName} ${appointment.clientLastName}`
      : "Unknown Client";

  const content = (
    <div className="flex gap-4">
      {/* Time Column */}
      <div className="shrink-0 w-20 flex flex-col items-center justify-center py-1">
        <span className="text-lg font-bold text-slate-800 tabular-nums">
          {startTime}
        </span>
        <span className="text-xs text-slate-400 font-medium">to</span>
        <span className="text-sm font-semibold text-slate-600 tabular-nums">
          {endTime}
        </span>
      </div>

      {/* Divider */}
      <div className="w-px bg-slate-200 self-stretch" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Dog name + Alert + Trainer */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-base font-bold text-slate-800 truncate">
            {appointment.dogName || "No dog"}
          </span>
          {appointment.dogBreed && (
            <span className="text-sm text-slate-500">{appointment.dogBreed}</span>
          )}
          {hasAlert && (
            <span className="text-sm" role="img" aria-label="Alert">
              ⚠️
            </span>
          )}
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {showTrainerBadge && appointment.calendarName && (
              <TrainerBadge
                initials={getTrainerInitials(appointment.calendarName)}
                color={appointment.calendarColor || "#6366F1"}
                name={appointment.calendarName}
              />
            )}
          </div>
        </div>

        {/* Row 2: Service + Owner */}
        <div className="flex items-center gap-3 mb-2">
          {appointment.serviceName && (
            <ServiceBadge
              service={appointment.serviceName}
              color={appointment.serviceColor}
            />
          )}
          <span className="text-sm text-slate-600">{clientName}</span>
        </div>

        {/* Row 3: Location */}
        <div className="flex items-center gap-1.5 text-slate-500">
          <LocationIcon type={appointment.locationType || "unknown"} size={14} />
          <span className="text-sm truncate">{getLocationLabel(appointment)}</span>
        </div>
      </div>
    </div>
  );

  const cardClasses =
    "block w-full bg-white rounded-lg border border-slate-200 p-4 text-left transition-all hover:shadow-md hover:border-slate-300 no-underline";

  const cardStyle = {
    borderLeftWidth: "4px",
    borderLeftColor: borderColor,
  };

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`${cardClasses} cursor-pointer active:scale-[0.99]`}
        style={cardStyle}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={`/appointments/${appointment.id}`}
      className={cardClasses}
      style={cardStyle}
    >
      {content}
    </Link>
  );
}
